/**
 * What to say when a search finds nothing.
 *
 * `screens.md` §3 lists the zero-results state as *"guesses the likeliest cause, which on
 * this data is nearly always Excel"*, and that instruction is worth reading twice: the
 * design is not asking for a friendlier "no results" — it is asking the screen to **name
 * the specific way this user's data got broken** before they go looking for a product that
 * is sitting in the catalogue under a number a spreadsheet rewrote.
 *
 * A function rather than JSX for the reason `attentionStack` is: which guess a query earns
 * is a decision about a string, and a decision about a string should not need a browser to
 * check. It is also the piece most likely to be got wrong quietly — every branch below
 * renders a confident sentence, so a misclassification does not look like a bug, it looks
 * like advice.
 *
 * ## The guesses are ordered by how specific the evidence is
 *
 * Scientific notation is near-certain (nothing else produces `8.71E+12`); a 13-digit number
 * is a strong hint (a GTIN-14 that lost its leading zero on the way through a cell
 * formatted as a number); a short digit run is no evidence at all and gets the honest
 * generic answer rather than a guess dressed as one.
 */

/** `8.71E+12`, `8.5002E+12` — a cell that was formatted as a number. */
const SCIENTIFIC = /^\d(\.\d+)?e\+?\d+$/i;

const ALL_DIGITS = /^\d+$/;

/** `Lot 42`, `lot42`, `LOT-42` — the query names a lot. */
const LOT_WORD = /^lot\b|^lot\d/i;

// Capitals and digits with no space or lowercase: `B7`, `A123X`. Not `2x250g` or `E45 cream`,
// which are product names (E.2 in `product-list.test.tsx`).
const LOT_SHAPE = /^(?=[^a-z\s]*\d)(?=[^a-z\s]*[A-Z])[^a-z\s]{2,20}$/;

export interface SearchCause {
  /** The headline — what probably happened, in the user's terms. */
  readonly title: string;
  /** The detail: what to check, or why we cannot say. */
  readonly body: string;
}

export function zeroResultCause(query: string): SearchCause {
  const q = query.trim();

  if (SCIENTIFIC.test(q)) {
    return {
      title: "That looks like a spreadsheet's shorthand for a long number",
      body:
        `Excel rewrites a 14-digit code as ${q} once the column is formatted as a ` +
        `number, and the original digits are gone from the file — not hidden, gone. ` +
        `Open the source spreadsheet, format that column as Text, and re-enter the code; ` +
        `searching for the shorthand will never find it here, because it was never what ` +
        `we were sent.`,
    };
  }

  if (ALL_DIGITS.test(q) && q.length === 13) {
    return {
      title: "Thirteen digits — a GTIN-14 that lost its leading zero",
      body:
        `A GTIN-14 beginning with 0 comes out of a spreadsheet as 13 digits, because a ` +
        `cell formatted as a number has no way to keep it. Try searching for ${q} ` +
        `without the first digit — a code matches anywhere inside the number, so a ` +
        `partial search finds it whichever end was trimmed.`,
    };
  }

  if (ALL_DIGITS.test(q) && q.length >= 14) {
    return {
      title: "No product carries that code",
      body:
        `The search matches a code anywhere inside it, so a typo in the middle of a long ` +
        `number still finds nothing. Try the last four or five digits instead — that is ` +
        `usually enough to be unique and short enough to read off the packet.`,
    };
  }

  if (LOT_WORD.test(q) || LOT_SHAPE.test(q)) {
    return {
      title: "No lot on file is spelled exactly like that",
      body:
        `A lot code matches a batch record exactly — capitals, spaces and punctuation ` +
        `count, so LOT42 and Lot 42 are different lots to a scanner. Copy the code from ` +
        `the pack as it is printed. Names, SKUs and GTINs still match by part.`,
    };
  }

  return {
    title: "Nothing matches that",
    body:
      `The search covers product names in every language, SKUs, and codes by substring, ` +
      `and lot codes exactly as printed. If you are looking for a product you know ` +
      `exists, try a shorter piece of it — and ` +
      `check the status filter, which narrows the list before the search runs.`,
  };
}
